/**
 * Profile → "Auth token" — super admins only.
 *
 * Shows the signed-in session's token (the same one every API call sends) so a
 * super admin can paste it into a script or a Claude Code session that calls the
 * API as them (e.g. the /request-board-* commands). Masked until revealed,
 * one-click copy, and the expiry read from the token itself so a stale paste is
 * obvious. Nothing is sent anywhere; the token is read from this browser's
 * storage on demand.
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { useSnackbar } from "notistack";

import { getAuthToken } from "../../lib/digitalSizerHttp";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { status } from "../../theme/colors";
import { T } from "../SupportScreen/components/supportUi";

/** The token's expiry (from its `exp` claim), or null if it can't be read. */
const tokenExpiry = (token) => {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), "=")));
    return exp ? new Date(exp * 1000) : null;
  } catch {
    return null;
  }
};

const mask = (token) => `${token.slice(0, 12)}${"•".repeat(24)}${token.slice(-6)}`;

const ghostBtn = {
  color: T.ink700,
  bgcolor: "#fff",
  borderColor: T.line,
  fontWeight: 600,
  borderRadius: "8px",
  "&:hover": { borderColor: T.ink300, bgcolor: "#f8fafb" },
};

export default function ProfileAuthTokenCard({ Card }) {
  const { enqueueSnackbar } = useSnackbar();
  const [shown, setShown] = useState(false);
  // Re-read on every render so a token refreshed in the background shows here.
  const token = getAuthToken() || "";
  const expiry = useMemo(() => (token ? tokenExpiry(token) : null), [token]);
  const expired = !!expiry && expiry.getTime() <= Date.now();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      enqueueSnackbar("Auth token copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't copy the token", { variant: "error" });
    }
  };

  return (
    <Card
      title="Auth token"
      subtitle="Your sign-in token, for scripts and tools that act as you. Treat it like your password: anyone with it can use the portal as you until it expires."
      action={
        token ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={shown ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
              onClick={() => setShown((v) => !v)}
              sx={ghostBtn}
            >
              {shown ? "Hide" : "Show"}
            </Button>
            <Button variant="contained" startIcon={<ContentCopyRounded />} onClick={copy} sx={{ borderRadius: "8px", fontWeight: 600 }}>
              Copy
            </Button>
          </Box>
        ) : null
      }
    >
      {token ? (
        <>
          <Box
            sx={{
              p: "10px 12px",
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              bgcolor: "#f8fafb",
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 12,
              color: T.ink700,
              wordBreak: "break-all",
              maxHeight: 120,
              overflowY: "auto",
              userSelect: shown ? "text" : "none",
            }}
          >
            {shown ? token : mask(token)}
          </Box>
          <Box sx={{ mt: 1, fontSize: 12.5, color: expired ? status.error.fg : T.ink500 }}>
            {expiry
              ? expired
                ? `Expired ${formatDateTime(expiry.toISOString(), "datetime")}. Sign out and back in for a new one.`
                : `Expires ${formatDateTime(expiry.toISOString(), "datetime")}. Signing out ends it sooner.`
              : "The expiry couldn't be read from this token."}
          </Box>
        </>
      ) : (
        <Box sx={{ fontSize: 13, color: T.ink500 }}>No token found for this session.</Box>
      )}
    </Card>
  );
}

ProfileAuthTokenCard.propTypes = { Card: PropTypes.elementType.isRequired };
